import { observer } from "mobx-react";
import { EnterpriseAccountsStore } from "./enterpriseAccountsDomain";
import { FC } from "react";
import { Box, Card, VBox } from "@/components-v2/Box";
import { useDispatch, useTranslation } from "@/stores/RootStoreContext";
import { Dialog } from "@/components-v2/Dialog";
import { MTextInput } from "@/components-v2/TextInput";
import { Icon } from "@/components-v2/Icon";
import { Tag } from "@/components-v2/Tag";
import { Button, LinkButton } from "@/components-v2/Button";
import { isOk } from "@/common/common";
import { loadUrl } from "@/events/eventsDomain";

const supportEmail = "support@dcompany.no";

export const SharepointSettingsDialog: FC<{ store: EnterpriseAccountsStore }> = observer(
  ({ store }) => {
    const { t } = useTranslation();
    if (!(store.integrationSettingsModel?.type === "sharepoint")) {
      return null;
    }
    return (
      <Dialog
        title={t("sharepoint.connect-site")}
        onCancel={() => store.toggleSharepointSettingsDialog()}
        onSubmit={() => store.addOrUpdateSharepointSettings()}
        submitLabel={t("sharepoint.connect")}
        submitResult={store.submitSharepointSettingsResult}
      >
        <VBox gap={"lg"}>
          {t("sharepoint.connect-site.help-text")}
          <MTextInput
            model={store.integrationSettingsModel.siteID}
            label={t("sharepoint.siteID")}
          />
          <MTextInput
            model={store.integrationSettingsModel.clientID}
            label={t("sharepoint.clientID")}
          />
          <MTextInput
            model={store.integrationSettingsModel.clientSecret}
            label={t("sharepoint.clientSecret")}
          />
          <MTextInput
            model={store.integrationSettingsModel.tenantID}
            label={t("sharepoint.tenantID")}
          />
        </VBox>
      </Dialog>
    );
  },
);

export const SharepointSetupDialog: FC<{ store: EnterpriseAccountsStore }> = observer(
  ({ store }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    if (!store.sharepointSetupSiteUrl) {
      return null;
    }
    return (
      <Dialog
        title={t("integrations.sharepoint.configure")}
        onCancel={() => store.toggleSharepointSetupDialog()}
        onSubmit={() => {
          const url = store.sharepointSetupUrl();
          if (url) dispatch([loadUrl(url, false)]);
        }}
        submitLabel={t("integrations.sharepoint.setup.submit")}
      >
        <VBox gap={"lg"}>
          {t("integrations.sharepoint.setup.help-text")}
          <MTextInput
            model={store.sharepointSetupSiteUrl}
            label={t("integrations.sharepoint.site-url")}
            placeholder={"https://contoso.sharepoint.com/sites/dcompany"}
          />
        </VBox>
      </Dialog>
    );
  },
);

export const SharepointSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  const isActive =
    isOk(store.sharepointSettingsResult) && store.sharepointSettingsResult.data !== undefined;
  const openDialog = () => {
    store.submitSharepointSettingsResult = undefined;
    store.toggleSharepointSettingsDialog();
  };
  return (
    <Card padding gap={"lg"} maxWidth={"xl"} grow={"golden"} justifyContent={"center"}>
      <Box justifyContent={"space-between"} alignItems={"start"}>
        <Box gap={"sm"} alignItems={"center"}>
          <h4>{t("integrations.sharepoint")}</h4>
          <Tag
            color={isActive ? "success" : "neutral"}
            label={isActive ? t("integrations.active") : t("inactive")}
            compact
          />
        </Box>
        <Box gap={"xs"} alignItems={"center"}>
          {store.isSharepointConnectionVerified && (
            <Icon name={"check"} size={"sm"} color={"success"} weight={"bold"} />
          )}
          {isActive && store.canSetupSharepoint && (
            <Button
              variant={"secondary"}
              size={"sm"}
              label={t("integrations.sharepoint.test-connection")}
              loading={store.sharepointConnectionTestPending}
              disabled={store.sharepointConnectionTestPending}
              onClick={() => store.testSharepointConnection()}
              fullWidth={false}
            />
          )}
          {store.canSetupSharepoint && (
            <Button
              variant={"secondary"}
              size={"sm"}
              label={
                isActive
                  ? t("integrations.sharepoint.reconfigure")
                  : t("integrations.sharepoint.configure")
              }
              onClick={() => store.toggleSharepointSetupDialog()}
              fullWidth={false}
            />
          )}
          {store.hasSuperAdminAccess &&
            (isActive ? (
              <LinkButton
                weight={"normal"}
                size={"sm"}
                icon={"pen"}
                label={t("edit")}
                onClick={openDialog}
              />
            ) : (
              <Button
                variant={"secondary"}
                size={"sm"}
                label={t("integrations.sharepoint.configure-manually")}
                onClick={openDialog}
                fullWidth={false}
              />
            ))}
          {!isActive && !store.hasSuperAdminAccess && !store.canSetupSharepoint && (
            <Box gap={"xs"} textSize={"sm"}>
              {t("integrations.sharepoint.contact-support")}
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </Box>
          )}
        </Box>
      </Box>
      <div>{t("integrations.sharepoint.description")}</div>
      {!isActive && (
        <Box>
          <LinkButton
            size={"sm"}
            label={t("learn-more")}
            weight={"normal"}
            onClick={[loadUrl("https://dcompany.no/docs/sharepoint")]}
          />
        </Box>
      )}
    </Card>
  );
});
